import { Outlet, createFileRoute, linkOptions } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { Upload } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type DateFilter,
  type InsistenceFilters,
  type InsistenceResponse,
  type InsistenceSortingState,
  InsistenceType,
  insistenceSortingColumns,
} from 'types'
import { z } from 'zod'

import { Button } from '@shadcn/button'

import {
  queryInsistenceCount,
  queryInsistenceList,
} from '@api/insistence/insistence.queries'

import StreamingExportDialog, {
  ExportType,
} from '@/components/export/streaming-export-dialog'
import InsistencesImportDialog from '@/components/insistence/insistences-import-dialog'
import PortfolioSelect from '@/components/portfolio-select'
import SearchBar from '@/components/search-bar'
import type { CursorPaginationState } from '@/components/table/cursor-pagination'
import DateFilterDialog from '@/components/table/date-filter-dialog'
import InsistencesTable from '@/components/tables/insistences/insistences-table'
import { ViewTransitionType } from '@/css/view-transition.types'
import { dateFilterSchema, numberFilterSchema } from '@/lib/filter'
import { cursorPaginationSchema, sortingSchema } from '@/lib/table'
import { pageSearchAtom, storePageSearch } from '@/stores/page-search.atom'
import { portfolioFilterAtom } from '@/stores/portfolio-filter.atom'

import SectionHeader from '../../-components/section-layout/section-header'

const searchSchema = z.object({
  pagination: cursorPaginationSchema,
  sorting: sortingSchema(insistenceSortingColumns).transform((sorting) =>
    sorting?.length
      ? sorting
      : [
          {
            id: 'date',
            desc: true,
          },
        ],
  ),
  filters: z
    .object({
      search: z.string().optional(),
      date: dateFilterSchema.optional(),
      type: z.enum(InsistenceType).optional(),
      insured: z.string().optional(),
      debtor: z.string().optional(),
      group: z.string().optional(),
      country: z.string().optional(),
      policyTypeCode: z.string().optional(),
      policy: z.string().optional(),
      currency: z.string().optional(),
      requestedAmount: numberFilterSchema.optional(),
    })
    .optional(),
})

type Search = z.infer<typeof searchSchema>

const rowLink = (row: InsistenceResponse) => {
  const coverageUuid = row.resultingCoverageUuid ?? row.initialCoverageUuid
  if (!coverageUuid) return null

  return linkOptions({
    to: '/app/data/insistences/$coverageUuid',
    viewTransition: { types: [ViewTransitionType.SheetParentToChild] },
    params: { coverageUuid },
  })
}

export const Route = createFileRoute('/app/data/insistences')({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({
    context: { queryClient },
    deps: { pagination, sorting, filters },
  }) => {
    queryClient.prefetchQuery(
      queryInsistenceList({
        ...pagination,
        sorting,
        filters,
      }),
    )
    queryClient.prefetchQuery(queryInsistenceCount(filters))
  },
  beforeLoad: ({ search, cause, location }) => {
    storePageSearch(
      Route.fullPath,
      { to: Route.fullPath, search },
      location,
      cause,
    )
  },
})

function RouteComponent() {
  const { t } = useTranslation()
  const routeSearch = Route.useSearch()
  const navigate = Route.useNavigate()
  const portfolioFilter = useAtomValue(portfolioFilterAtom)
  const [importDialogOpen, setImportDialogOpen] = useState(false)

  const filters: InsistenceFilters = {
    ...routeSearch.filters,
    portfolioUserUuid: portfolioFilter,
  }

  // If user is on a coverage detail sheet,
  // don't change the search params to keep
  // the filters and sorting applied on the table.
  const pageSearch = useAtomValue(pageSearchAtom)
  const tableFrozen = false
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const search = tableFrozen
    ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      ((pageSearch[Route.fullPath]?.search as undefined | Search) ??
      routeSearch)
    : routeSearch

  const setFilters = (
    updater: (filter?: InsistenceFilters) => InsistenceFilters,
  ) =>
    navigate({
      search: (previous) => ({
        ...previous,
        filters: updater(previous.filters),
      }),
    })

  const setPagination = (
    updater: (pagination: CursorPaginationState) => CursorPaginationState,
  ) =>
    navigate({
      search: (previous) => ({
        ...previous,
        pagination: updater(previous.pagination),
      }),
    })

  const setSorting = (
    updater: (sorting?: InsistenceSortingState) => InsistenceSortingState,
  ) =>
    navigate({
      search: (previous) => ({
        ...previous,
        sorting: updater(previous.sorting),
      }),
    })

  const setSearchText = (search: string) =>
    setFilters((previous) => ({ ...previous, search }))

  const setDateFilter = (date?: DateFilter) =>
    setFilters((previous) => ({ ...previous, date }))

  return (
    <div className="flex h-full grow flex-col divide-y overflow-hidden">
      <SectionHeader isSecondary title={t('data.insistences.title')}>
        <div className="flex items-center gap-2">
          <SearchBar
            tabIndex={1}
            className="max-w-62.75"
            text={filters.search}
            setText={setSearchText}
          />
          <div className="w-47.75">
            <DateFilterDialog
              placeholder={t('table.columnFilters.date.placeholder')}
              onChange={setDateFilter}
              value={filters.date}
              triggerButtonProps={dateFilterTriggerButtonProps}
            />
          </div>
          <PortfolioSelect />
          <Button size="sm" onClick={() => setImportDialogOpen(true)}>
            <Upload className="size-4" />
            <span>Import</span>
          </Button>
          <StreamingExportDialog
            type={ExportType.Insistences}
            filters={filters}
          />
          <InsistencesImportDialog
            onOpenChange={setImportDialogOpen}
            open={importDialogOpen}
          />
        </div>
      </SectionHeader>
      <div className="flex h-full flex-col gap-4 overflow-hidden p-2">
        <InsistencesTable
          filters={filters}
          setFilters={setFilters}
          pagination={search.pagination}
          setPagination={setPagination}
          sorting={search.sorting}
          setSorting={setSorting}
          rowLink={rowLink}
        />
        <Outlet />
      </div>
    </div>
  )
}

const dateFilterTriggerButtonProps = {
  size: 'sm',
} as const
